/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { LicensedHardwareDTO } from "../../models/licensed-hardware-dto";
import { CompiledLicenseDTO } from "../../models/compiled-license-dto";
import { LicenseDTO } from "../../models/license-dto";
import { ConfirmDeleteDialogComponent } from "../confirm-delete-dialog.component";
import { AuthenticationService } from '../../services/authentication.service';
import { LicensingService } from "../../services/licensing.service";
import { ViewLicenseDialogComponent } from './view-license-dialog.component';
import { ViewCompiledLicenseDialogComponent } from './view-compiled-license-dialog.component';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from '../simple-success-error-dialog-box/simple-success-error-dialog-box.component';

declare var $: any;

@Component({
  selector: 'app-licensing',
  templateUrl: './licensing.component.html',
  styleUrls: ['./licensing.component.css']
})
export class LicensingComponent implements OnInit {

  mCompiledLicense: CompiledLicenseDTO;
  mLicenseUIs: LicenseUI[] = [];
  mAvailableHardware: LicensedHardwareDTO[] = [];


  @ViewChild('licenseFile')
  mLicenseFileInput: ElementRef;

  @ViewChild(ConfirmDeleteDialogComponent)
  mConfirmDeleteDialog: ConfirmDeleteDialogComponent;

  @ViewChild(ViewLicenseDialogComponent)
  mViewLicensingDialogComponent: ViewLicenseDialogComponent;

  @ViewChild(ViewCompiledLicenseDialogComponent)
  mViewCompiledLicenseDialogComponent: ViewCompiledLicenseDialogComponent;

  /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;


  mLicenseToView: LicenseDTO = null;


  constructor(
    private mRoute: ActivatedRoute,
    private mLicensingService: LicensingService,
    private mAuthenticationService: AuthenticationService 
  ) { }

  ngOnInit(): void {
    this.mRoute.data.subscribe(
      (data: 
        {
          availableHardware: LicensedHardwareDTO[],
          compiledLicense: CompiledLicenseDTO,
          licenses: LicenseDTO[]
        }
      ) => {
        this.mAvailableHardware = data.availableHardware == null? [] : data.availableHardware;
        this.mCompiledLicense = data.compiledLicense;
        this.setLicenses(data.licenses);
      }
    );
  }

  setLicenses(licenses: LicenseDTO[]): void {
    const uis: LicenseUI[] = [];
    if (licenses != null) {
      licenses.forEach(license => {
        const ui = new LicenseUI();
        ui.mLicense = license;
        uis.push(ui);
      });
    }
    this.mLicenseUIs = uis;
  }

  async refreshLicenseInformation(): Promise<void> {
    try {
      this.mCompiledLicense = await this.mLicensingService.getCompiledLicense();
    } catch (error) {
      this.handleError(error);
    }

    try {
      this.setLicenses(await this.mLicensingService.getInstalledLicenses());
    } catch (error) {
      this.handleError(error);
    }
  }

  isCompiledLicenseValid(): boolean {
    return this.mCompiledLicense != null && this.mCompiledLicense.is_valid == true;
  }

  getLicenseHeaderText(): string {
    return this.isCompiledLicenseValid()? " THE SYSTEM HAS AT LEAST ONE VALID LICENSE" : " NO LICENSES ARE VALID"; 
  }

  getSelectedLicenseUis(): LicenseUI[] {
    return this.mLicenseUIs.filter(ui => ui.mSelected);
  }

  isSingleLicenseUiSelected(): boolean {
    return this.getSelectedLicenseUis().length == 1;
  }

  isAnyLicenseUisSelected(): boolean {
    return this.mLicenseUIs.some(ui => ui.mSelected);
  }

  isAllLicenseUisSelected(): boolean {
    return this.mLicenseUIs.length > 0 && this.mLicenseUIs.every(ui => ui.mSelected);
  }

  setAllLicenseUisSelected(event: any): void {
    this.mLicenseUIs.forEach(ui => ui.mSelected = event.target.checked);
  }

  viewLicenseClicked(): void {
    const selectedUis = this.getSelectedLicenseUis();
    if (selectedUis.length == 1) {
      this.viewLicense(selectedUis[0]);
    }
  }

  viewLicense(ui: LicenseUI): void {
    this.mLicenseToView = ui.mLicense;
    this.mViewLicensingDialogComponent.showDialog();
  }

  deleteLicenseClicked(): void {
    const selectedUis = this.getSelectedLicenseUis();
    if (selectedUis.length > 0) {
      const ids = selectedUis.map(ui => ui.mLicense.id.toString(10));
      this.mConfirmDeleteDialog.show("license", ids);
    }
  }

  deleteConfirmed(): void {
    const selectedUis = this.getSelectedLicenseUis();
    if (selectedUis.length > 0) {
      const ids = selectedUis.map(ui => ui.mLicense.id);
      this.mLicensingService
          .deleteInstalledLicenses(ids)
          .then(
            async () => {
              await this.mAuthenticationService.refreshLicense();
              this.refreshLicenseInformation();
            },
            error => {
              console.error("Error encountered deleting selected Licenses.", error);
              this.handleError(error);
            }
          );
    }
  }

  showLicenseUploader(): void {
    this.mLicenseFileInput.nativeElement.click();
  }

  onFileChange(event: any): void {
    if (event.target.files.length === 1) {
      const formData = new FormData();
      formData.append("file", event.target.files[0]);
      this.mLicensingService
        .installLicense(formData)
        .then(
          async () => {
            await this.mAuthenticationService.refreshLicense();
            this.mLicenseFileInput.nativeElement.value = "";
            this.refreshLicenseInformation();
          },
          error => {
            console.error("Error encountered submitting License File.", error);
            if (error != null) {
              $("#invalidLicenseModal").modal('show');
              this.mLicenseFileInput.nativeElement.value = "";
            }
          }
        );
    }
  }

  viewCompiledLicenseClicked(): void {
    console.log("View Compiled License Clicked!");
    this.mViewCompiledLicenseDialogComponent.showDialog();
  }

  handleError(error: any): void {
    console.error("Error encountered.", error);
    if (error != null) {
      const response: Response = error;
      this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, response.text().toString());
    }
  }

}

class LicenseUI {
  mSelected = false
  mLicense: LicenseDTO;

  getValidText(): string {
    return this.mLicense != null && this.mLicense.is_valid? "VALID" : "INVALID";
  }
  getValidForServerText(): string {
    return this.mLicense != null && this.mLicense.is_valid_for_server? "VALID" : "INVALID";
  }
  getExpiredText(): string {
    return this.mLicense != null && this.mLicense.is_expired? "EXPIRED" : "NO";
  }

}
